import React, { useEffect, useState, useCallback } from 'react';
import { useAsync } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';

import selectors from 'state/selectors';

import Header from '../../components/Header';
import Mail from './Mail';
import Disk from './Disk';
import Upload from './Upload';

import utils from 'utils';
import CommonButton from 'components/Buttons/CommonButton';
import actions from 'state/actions';
import services from 'services';
import Toggle from 'components/Input/Toggle';

import styled from 'styled-components/macro';
import { RangeDate } from '../dashboard';
import Einvoice from './Einvoice';
import { CHANNELS_MAP } from 'utils/constants';

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.label`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizePalette.small};
  color: ${({ theme }) => theme.colorPalette.turquoise.normal};
`;

const Monitor = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [rangeDateActive, setRangeDateActive] = useState<boolean>(false);
  const [rangeDate, setRangeDate] = useState<RangeDate>({ from: null, to: null });
  const tab = useSelector(selectors.normalizer.getTab);
  const dispatch = useDispatch();
  const selectedRow = useSelector(selectors.normalizer.getSelectedMonitorRow);
  const [hasHistory, setHasHistory] = useState(true);
  const t = utils.intl.useTranslator();

  const clearHistoryTable = useCallback(() => {
    dispatch(actions.normalizer.setMonitorMailHistory([]));
    dispatch(actions.normalizer.setMonitorMailWfHistory([]));

    dispatch(actions.normalizer.setDocumentHistory([]));
    dispatch(actions.normalizer.setMonitorDiskWfHistory([]));

    dispatch(actions.normalizer.setDocumentHistoryUpload([]));
    dispatch(actions.normalizer.setMonitorUploadWfHistory([]));

    dispatch(actions.normalizer.setDocumentHistoryEinvoice([]));
    dispatch(actions.normalizer.setMonitorEInvoiceWfHistory([]));
  }, [dispatch]);

  useAsync(async () => {
    if (rangeDateActive && !(rangeDate.from || rangeDate.to)) return;

    let dateFrom;
    let dateTo;

    if (rangeDateActive) {
      dateFrom = rangeDate.from ? utils.date.formatDateForDb(rangeDate.from) : '01-01-1970';
      dateTo = utils.date.formatDateForDb(rangeDate.to ? rangeDate.to : new Date());
    } else {
      dateFrom = utils.date.formatDateForDb(date);
      dateTo = utils.date.formatDateForDb(date);
    }

    dispatch(actions.normalizer.setSelectedMonitorRow([]));
    clearHistoryTable();

    // Mail
    try {
      const { data: mainTable } = await services.getMailMonitor(dateFrom, dateTo);
      dispatch(actions.normalizer.setMonitorMailRows(mainTable));
    } catch (e) {
      console.error(e);
    }
    // Disk
    try {
      const { data } = await services.getDiskMonitor({
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
      dispatch(actions.normalizer.setDiskMonitor(data));
    } catch (e) {
      console.error(e);
    }
    // Upload
    try {
      const { data } = await services.getFileUploadMonitor(dateFrom, dateTo);
      dispatch(actions.normalizer.setUploadMonitor(data));
    } catch (e) {
      console.error(e);
    }
    // E-invoice
    try {
      const { data } = await services.getEInvoiceMonitors({
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
      dispatch(actions.normalizer.setEInvoiceMonitor(data));
    } catch (e) {
      console.error(e);
    }
  }, [date, rangeDate, rangeDateActive]);

  useEffect(() => {
    clearHistoryTable();
  }, [selectedRow, clearHistoryTable]);

  useAsync(async () => {
    const protocol = selectedRow[0].protocol;

    // history table on new selected row
    if (tab === 'mail' && protocol && hasHistory) {
      try {
        const { data: historyTable } = await services.getMailDocumentHistory(Number(protocol));
        const { data: wfhistoryTable } = await services.getWfHistory(Number(protocol));

        dispatch(actions.normalizer.setMonitorMailHistory(historyTable));
        dispatch(actions.normalizer.setMonitorMailWfHistory(wfhistoryTable));
      } catch (e) {
        console.error(e);
      }
    } else {
      try {
        if (protocol && hasHistory) {
          const { data: historyTable } = await services.getDocumentHistory(Number(protocol));
          const { data: wfhistoryTable } = await services.getWfHistory(Number(protocol));

          if (tab === 'disk') {
            dispatch(actions.normalizer.setDocumentHistory(historyTable));
            dispatch(actions.normalizer.setMonitorDiskWfHistory(wfhistoryTable));
          }
          if (tab === 'upload') {
            dispatch(actions.normalizer.setDocumentHistoryUpload(historyTable));
            dispatch(actions.normalizer.setMonitorUploadWfHistory(wfhistoryTable));
          }
          if (tab === 'eInvoice') {
            dispatch(actions.normalizer.setDocumentHistoryEinvoice(historyTable));
            dispatch(actions.normalizer.setMonitorEInvoiceWfHistory(wfhistoryTable));
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [selectedRow, hasHistory]);

  const downloadMail = async () => {
    try {
      const protocol = selectedRow[0].protocol;
      const { data } = await services.downloadMail(
        Number(protocol),
        tab === 'mail' ? CHANNELS_MAP.MAIL : CHANNELS_MAP.EINVOICE,
      );
      utils.file.downloadFileFromBase64Source('mail', data, 'message/rfc822', 'eml');
    } catch (e) {
      console.error(e);
    }
  };

  const showHistoryTable = hasHistory && selectedRow.length > 0;

  return (
    <>
      <Header
        date={date}
        setDate={setDate}
        rangeDateActive={rangeDateActive}
        setRangeDateActive={setRangeDateActive}
        rangeDate={rangeDate}
        setRangeDate={setRangeDate}
      >
        {tab === 'mail' || tab === 'eInvoice' ? (
          <FlexContainer>
            <FlexContainer>
              <Label>{t('show-history')}</Label>
              <Toggle checked={hasHistory} onChange={() => setHasHistory(!hasHistory)} disabled />
            </FlexContainer>
            <CommonButton
              id="monitor-download-mail-btn"
              disabled={selectedRow.length === 0}
              customIcon="download"
              scope="tertiary"
              value={t('download-mail')}
              action={() => downloadMail()}
            />
          </FlexContainer>
        ) : (
          <FlexContainer>
            <Label>{t('show-history')}</Label>
            <Toggle checked={hasHistory} onChange={() => setHasHistory(!hasHistory)} disabled />
          </FlexContainer>
        )}
      </Header>

      {tab === 'mail' ? (
        <Mail hasHistory={showHistoryTable} />
      ) : tab === 'disk' ? (
        <Disk hasHistory={showHistoryTable} />
      ) : tab === 'upload' ? (
        <Upload hasHistory={showHistoryTable} />
      ) : tab === 'eInvoice' ? (
        <Einvoice hasHistory={showHistoryTable} />
      ) : null}
    </>
  );
};

export default Monitor;
